// pages/admin/ConfiguracionAdmin.jsx
import React, { useState, useEffect } from 'react';
import AdminLayout from '../../layouts/AdminLayout';
import { 
  Plus, Edit, Trash2, X, Check, 
  DollarSign, Smartphone, CreditCard, 
  Building2, User, Banknote, Settings, RefreshCw, Mail
} from 'lucide-react';
import Modal from '../../components/Modal';
import ModalConfirm from '../../components/ModalConfirm';
import { showSuccess, showError, showLoading, dismissToast } from '../../utils/toast';

const API_URL = process.env.REACT_APP_API_URL;

const ConfiguracionAdmin = () => {
  const [metodos, setMetodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMetodoModal, setShowMetodoModal] = useState(false);
  const [showCuentaModal, setShowCuentaModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [editingMetodo, setEditingMetodo] = useState(null);
  const [editingCuenta, setEditingCuenta] = useState(null);
  const [selectedMetodoId, setSelectedMetodoId] = useState(null);
  const [confirmConfig, setConfirmConfig] = useState({
    title: '',
    message: '',
    onConfirm: null,
    confirmText: 'Sí, eliminar',
    confirmButtonColor: '#dc2626'
  });
  
  // ✅ Nombre + Moneda
  const [metodoForm, setMetodoForm] = useState({
    nombre: '',
    moneda: null // "VES" | "BRL" | "NINGUNA" | null
  });

  // ✅ Solo Banco, Clave, Titular, Email (opcional)
  const [cuentaForm, setCuentaForm] = useState({
    metodoPagoId: '',
    nombreBanco: '',
    clave: '',
    titular: '',
    email: ''
  });

  useEffect(() => {
    cargarMetodos();
  }, []);

  const cargarMetodos = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/metodospago`);
      if (!response.ok) throw new Error('Error al cargar métodos de pago');
      const data = await response.json();
      setMetodos(data);
    } catch (error) {
      console.error('Error:', error);
      showError('No se pudieron cargar los métodos de pago');
    } finally {
      setLoading(false);
    }
  };

  const handleMetodoSubmit = async (e) => {
    e.preventDefault();
    
    const toastId = showLoading('Guardando...');
    
    try {
      const url = editingMetodo 
        ? `${API_URL}/metodospago/${editingMetodo.id}`
        : `${API_URL}/metodospago`;
      
      const method = editingMetodo ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(metodoForm)
      });

      const responseText = await response.text();
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Error al parsear respuesta:', parseError);
        throw new Error('Respuesta del servidor inválida');
      }

      if (!response.ok) {
        throw new Error(data.message || 'Error al guardar');
      }

      setShowMetodoModal(false);
      setEditingMetodo(null);
      setMetodoForm({ nombre: '', moneda: null });
      
      await cargarMetodos();
      dismissToast(toastId);
      showSuccess(editingMetodo ? 'Método de pago actualizado' : 'Método de pago creado');
      
    } catch (error) {
      console.error('Error en handleMetodoSubmit:', error);
      dismissToast(toastId);
      showError(error.message || 'Ocurrió un error al guardar');
    }
  };

  const handleCuentaSubmit = async (e) => {
    e.preventDefault();
    
    if (!cuentaForm.metodoPagoId) {
      showError('Seleccioná un método de pago');
      return;
    }

    if (!cuentaForm.nombreBanco.trim()) {
      showError('El campo Banco es obligatorio');
      return;
    }
    if (!cuentaForm.clave.trim()) {
      showError('El campo Clave es obligatorio');
      return;
    }
    if (!cuentaForm.titular.trim()) {
      showError('El campo Titular es obligatorio');
      return;
    }

    const toastId = showLoading('Guardando cuenta...');
    
    try {
      const url = editingCuenta 
        ? `${API_URL}/metodospago/cuentas/${editingCuenta.id}`
        : `${API_URL}/metodospago/cuentas`;
      
      const method = editingCuenta ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cuentaForm)
      });

      const responseText = await response.text();
      console.log('📦 Respuesta del backend:', responseText);
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('❌ Error al parsear JSON:', parseError);
        throw new Error('Respuesta del servidor inválida');
      }

      if (!response.ok) {
        throw new Error(data.message || data.title || 'Error al guardar la cuenta');
      }

      setShowCuentaModal(false);
      setEditingCuenta(null);
      setSelectedMetodoId(null);
      setCuentaForm({
        metodoPagoId: '',
        nombreBanco: '',
        clave: '',
        titular: '',
        email: ''
      });
      
      await cargarMetodos();
      dismissToast(toastId);
      showSuccess(editingCuenta ? 'Cuenta actualizada correctamente' : 'Cuenta creada correctamente');
      
    } catch (error) {
      console.error('❌ Error en handleCuentaSubmit:', error);
      dismissToast(toastId);
      showError(error.message || 'Ocurrió un error al guardar la cuenta');
    }
  };

  const handleDeleteMetodo = (metodo) => {
    setConfirmConfig({
      title: '¿Eliminar método?',
      message: `¿Estás seguro de eliminar <strong>${metodo.nombre}</strong>?${metodo.cuentas?.length > 0 ? '<br/><br/>⚠️ Este método tiene cuentas asociadas. Se desactivará pero no se eliminará.' : ''}`,
      onConfirm: async () => {
        const toastId = showLoading('Eliminando...');
        
        try {
          const response = await fetch(`${API_URL}/metodospago/${metodo.id}`, {
            method: 'DELETE'
          });

          const responseText = await response.text();
          let data;
          try {
            data = JSON.parse(responseText);
          } catch {
            data = { message: responseText || 'Método eliminado' };
          }

          if (!response.ok) {
            throw new Error(data.message || 'Error al eliminar');
          }

          setShowConfirmModal(false);
          await cargarMetodos();
          dismissToast(toastId);
          showSuccess(data.message || 'Método de pago eliminado correctamente');
          
        } catch (error) {
          console.error('Error en handleDeleteMetodo:', error);
          setShowConfirmModal(false);
          dismissToast(toastId);
          showError(error.message || 'Ocurrió un error al eliminar');
        }
      },
      confirmText: 'Sí, eliminar',
      confirmButtonColor: '#dc2626'
    });
    setShowConfirmModal(true);
  };

  const handleDeleteCuenta = (cuenta) => {
    setConfirmConfig({
      title: '¿Eliminar cuenta?',
      message: `¿Estás seguro de eliminar la cuenta <strong>${cuenta.nombreBanco}</strong> - ${cuenta.clave}?`,
      onConfirm: async () => {
        const toastId = showLoading('Eliminando cuenta...');
        
        try {
          const response = await fetch(`${API_URL}/metodospago/cuentas/${cuenta.id}`, {
            method: 'DELETE'
          });

          const responseText = await response.text();
          let data;
          try {
            data = JSON.parse(responseText);
          } catch {
            data = { message: responseText || 'Cuenta eliminada' };
          }

          if (!response.ok) {
            throw new Error(data.message || 'Error al eliminar');
          }

          setShowConfirmModal(false);
          await cargarMetodos();
          dismissToast(toastId);
          showSuccess(data.message || 'Cuenta eliminada correctamente');
          
        } catch (error) {
          console.error('Error en handleDeleteCuenta:', error);
          setShowConfirmModal(false);
          dismissToast(toastId);
          showError(error.message || 'Ocurrió un error al eliminar');
        }
      },
      confirmText: 'Sí, eliminar',
      confirmButtonColor: '#dc2626'
    });
    setShowConfirmModal(true);
  };

  const openEditMetodo = (metodo) => {
    setEditingMetodo(metodo);
    setMetodoForm({ 
      nombre: metodo.nombre,
      moneda: metodo.moneda || null
    });
    setShowMetodoModal(true);
  };

  const openEditCuenta = (cuenta) => {
    setEditingCuenta(cuenta);
    setSelectedMetodoId(cuenta.metodoPagoId);
    setCuentaForm({
      metodoPagoId: cuenta.metodoPagoId,
      nombreBanco: cuenta.nombreBanco,
      clave: cuenta.clave,
      titular: cuenta.titular,
      email: cuenta.email || ''
    });
    setShowCuentaModal(true);
  };

  const openNewCuenta = (metodoId) => {
    setEditingCuenta(null);
    setSelectedMetodoId(metodoId);
    setCuentaForm({
      metodoPagoId: metodoId,
      nombreBanco: '',
      clave: '',
      titular: '',
      email: ''
    });
    setShowCuentaModal(true);
  };

  const getIcono = (nombre) => {
    switch(nombre) {
      case 'Efectivo': return <DollarSign className="w-5 h-5" />;
      case 'Transferencia': return <Smartphone className="w-5 h-5" />;
      case 'Pix': return <Smartphone className="w-5 h-5" />;
      case 'Tarjeta': return <CreditCard className="w-5 h-5" />;
      case 'Depósito': return <Banknote className="w-5 h-5" />;
      default: return <DollarSign className="w-5 h-5" />;
    }
  };

  // ✅ Helper para mostrar la moneda de forma legible
  const getMonedaLabel = (moneda) => {
    if (!moneda) return null;
    if (moneda === 'VES') return { texto: 'Bolívares', color: 'bg-emerald-100 text-emerald-700' };
    if (moneda === 'BRL') return { texto: 'Reales', color: 'bg-sky-100 text-sky-700' };
    if (moneda === 'NINGUNA') return { texto: 'Sin conversión', color: 'bg-gray-200 text-gray-600' }; // ✅ NUEVO
    return { texto: moneda, color: 'bg-gray-100 text-gray-700' };
  };

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Configuración</h1>
          <p className="text-gray-500 text-sm">Gestioná los métodos de pago y cuentas bancarias</p>
        </div>

        {/* Métodos de Pago */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-[#e83e8c]" />
              <h2 className="text-lg font-semibold text-gray-800">Métodos de Pago</h2>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={cargarMetodos}
                className="p-2 text-gray-500 hover:text-[#e83e8c] hover:bg-pink-50 rounded-lg transition-colors"
                title="Actualizar"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
              <button
                onClick={() => {
                  setEditingMetodo(null);
                  setMetodoForm({ nombre: '', moneda: null });
                  setShowMetodoModal(true);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-[#e83e8c] hover:bg-[#c2185b] text-white rounded-lg transition-colors text-sm"
              >
                <Plus className="w-4 h-4" />
                Nuevo método
              </button>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#e83e8c] mx-auto"></div>
              <p className="text-gray-500 mt-3">Cargando métodos de pago...</p>
            </div>
          ) : metodos.length === 0 ? (
            <div className="text-center py-12">
              <DollarSign className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No hay métodos de pago configurados</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {metodos.map((metodo) => {
                const monedaInfo = getMonedaLabel(metodo.moneda);
                return (
                  <div key={metodo.id} className="p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-pink-50 rounded-lg flex items-center justify-center text-[#e83e8c]">
                          {getIcono(metodo.nombre)}
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-800">{metodo.nombre}</h3>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${metodo.activo ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                              {metodo.activo ? 'Activo' : 'Inactivo'}
                            </span>
                            {monedaInfo && (
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${monedaInfo.color}`}>
                                {monedaInfo.texto}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openNewCuenta(metodo.id)}
                          className="text-sm text-[#e83e8c] hover:text-[#c2185b] flex items-center gap-1"
                        >
                          <Plus className="w-4 h-4" />
                          Agregar cuenta
                        </button>
                        <button
                          onClick={() => openEditMetodo(metodo)}
                          className="p-2 text-gray-400 hover:text-[#e83e8c] hover:bg-pink-50 rounded-lg transition-colors"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteMetodo(metodo)}
                          className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Cuentas bancarias asociadas */}
                    {metodo.cuentas && metodo.cuentas.length > 0 && (
                      <div className="mt-3 ml-14 space-y-2">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Cuentas asociadas</p>
                        {metodo.cuentas.map((cuenta) => (
                          <div key={cuenta.id} className="bg-gray-50 rounded-lg p-3 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Building2 className="w-4 h-4 text-gray-400" />
                              <div>
                                <p className="text-sm font-medium text-gray-800">{cuenta.nombreBanco}</p>
                                <p className="text-xs text-gray-500">Clave: {cuenta.clave}</p>
                                <p className="text-xs text-gray-400">Titular: {cuenta.titular}</p>
                                {cuenta.email && (
                                  <p className="text-xs text-gray-400">Email: {cuenta.email}</p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => openEditCuenta(cuenta)}
                                className="p-1.5 text-gray-400 hover:text-[#e83e8c] hover:bg-pink-50 rounded-lg transition-colors"
                              >
                                <Edit className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => handleDeleteCuenta(cuenta)}
                                className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ✅ Modal Método de Pago - Nombre + Moneda */}
        <Modal
          isOpen={showMetodoModal}
          onClose={() => setShowMetodoModal(false)}
          title={editingMetodo ? 'Editar método de pago' : 'Nuevo método de pago'}
          confirmText={editingMetodo ? 'Guardar' : 'Crear'}
          cancelText="Cancelar"
          onConfirm={handleMetodoSubmit}
          closeOnBackdrop={false}
        >
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Nombre del método *</label>
              <input
                type="text"
                value={metodoForm.nombre}
                onChange={(e) => setMetodoForm({...metodoForm, nombre: e.target.value})}
                placeholder="Ej: Transferencia, Pix, Efectivo"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-[#e83e8c] focus:ring-2 focus:ring-[#e83e8c]/20 focus:outline-none"
                required
              />
            </div>

            {/* ✅ Selector de moneda con opción NINGUNA */}
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Moneda / Conversión
              </label>
              <select
                value={metodoForm.moneda || ''}
                onChange={(e) => setMetodoForm({...metodoForm, moneda: e.target.value || null})}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-[#e83e8c] focus:ring-2 focus:ring-[#e83e8c]/20 focus:outline-none"
              >
                <option value="">Sin conversión específica (muestra todas)</option>
                <option value="VES">Solo Bolívares (VES)</option>
                <option value="BRL">Solo Reales (BRL)</option>
                <option value="NINGUNA">No mostrar ninguna conversión</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Define qué conversión mostrar al cliente cuando elija este método de pago.
              </p>
            </div>
          </div>
        </Modal>

        {/* ✅ Modal Cuenta Bancaria */}
        <Modal
          isOpen={showCuentaModal}
          onClose={() => setShowCuentaModal(false)}
          title={editingCuenta ? 'Editar cuenta bancaria' : 'Nueva cuenta bancaria'}
          confirmText={editingCuenta ? 'Guardar' : 'Crear'}
          cancelText="Cancelar"
          onConfirm={handleCuentaSubmit}
          closeOnBackdrop={false}
        >
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Banco *</label>
              <input
                type="text"
                value={cuentaForm.nombreBanco}
                onChange={(e) => setCuentaForm({...cuentaForm, nombreBanco: e.target.value})}
                placeholder="Ej: Banco de Venezuela, Nubank"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-[#e83e8c] focus:ring-2 focus:ring-[#e83e8c]/20 focus:outline-none"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                N° de transferencia / Clave / ID *
              </label>
              <input
                type="text"
                value={cuentaForm.clave}
                onChange={(e) => setCuentaForm({...cuentaForm, clave: e.target.value})}
                placeholder="Ej: 0102-12345678, 321321, usuario@banco"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-[#e83e8c] focus:ring-2 focus:ring-[#e83e8c]/20 focus:outline-none"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Titular *</label>
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={cuentaForm.titular}
                  onChange={(e) => setCuentaForm({...cuentaForm, titular: e.target.value})}
                  placeholder="Nombre completo del titular"
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-[#e83e8c] focus:ring-2 focus:ring-[#e83e8c]/20 focus:outline-none"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Email (opcional)</label>
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-gray-400" />
                <input
                  type="email"
                  value={cuentaForm.email}
                  onChange={(e) => setCuentaForm({...cuentaForm, email: e.target.value})}
                  placeholder="Email del titular (opcional)"
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-[#e83e8c] focus:ring-2 focus:ring-[#e83e8c]/20 focus:outline-none"
                />
              </div>
            </div>
          </div>
        </Modal>

        {/* Modal de Confirmación */}
        <ModalConfirm
          isOpen={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          onConfirm={confirmConfig.onConfirm}
          title={confirmConfig.title}
          message={confirmConfig.message}
          confirmText={confirmConfig.confirmText}
          confirmButtonColor={confirmConfig.confirmButtonColor}
        />
      </div>
    </AdminLayout>
  );
};

export default ConfiguracionAdmin;